import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch, ApiClientError } from '@/lib/api';
import { EmptyState } from '@/components/EmptyState';
import { Button } from '@/components/Button';
import type { SkillHistoryResponse } from './types';

const PAGE_SIZE = 50;

/**
 * Drawer「進捗履歴」タブ (`GET /employees/:employeeId/skills/history`、設計書§1-1/§1-2)。
 * 確定 (`POST /certifications/:id/confirm`) は新規行を作らず同一行の UPDATE のため、一覧では
 * レベル変更行の confirmed_by/confirmed_at が後から埋まる形で見える (設計書§1-2)。
 * ⚠️ APIレスポンスは snake_case (実装側連絡により訂正、./types.ts参照)。
 */
export function HistorySection({ employeeId, onForbidden }: { employeeId: string; onForbidden: () => void }) {
  const [offset, setOffset] = useState(0);
  const { data, isLoading, isError, error } = useQuery<SkillHistoryResponse>({
    queryKey: ['employee-skill-history', employeeId, offset],
    queryFn: () => apiFetch<SkillHistoryResponse>(`/employees/${employeeId}/skills/history?limit=${PAGE_SIZE}&offset=${offset}`),
    enabled: !!employeeId,
    retry: false,
  });

  useEffect(() => {
    if (isError && error instanceof ApiClientError && error.status === 403) onForbidden();
  }, [isError, error, onForbidden]);

  if (isLoading) return <div role="status" className="text-text-sub">読み込み中…</div>;
  if (isError) return <p role="alert" className="text-sm text-alert-text">進捗履歴の取得に失敗しました。</p>;

  const entries = data?.history ?? [];
  if (entries.length === 0 && offset === 0) return <EmptyState message="変更履歴がまだありません" />;

  return (
    <div className="space-y-2">
      <div className="border border-surface-border rounded-lg overflow-hidden bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-surface-sunken text-text-sub">
            <tr>
              <th className="text-left px-3 py-2" scope="col">日付</th>
              <th className="text-left px-3 py-2" scope="col">スキル</th>
              <th className="text-left px-3 py-2" scope="col">変更</th>
              <th className="text-left px-3 py-2" scope="col">確定</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-border">
            {entries.map((e) => (
              <tr key={`${e.employee_skill_id}-${e.effective_from}`}>
                <td className="px-3 py-2 align-top whitespace-nowrap">{e.effective_from}</td>
                <td className="px-3 py-2 align-top break-words">{e.skill_label ?? '—'}</td>
                <td className="px-3 py-2 align-top break-words">
                  {e.previous_skill_level_label
                    ? `${e.previous_skill_level_label} → ${e.skill_level_label ?? '—'}`
                    : (e.skill_level_label ?? '—')}
                </td>
                <td className="px-3 py-2 align-top text-xs text-text-sub whitespace-nowrap">
                  {e.confirmation_status === 'confirmed'
                    ? `確定済み${e.confirmed_by ? ` (${e.confirmed_by})` : ''}`
                    : '未確定'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {(offset > 0 || data?.has_more) && (
        <div className="flex items-center justify-between">
          <Button variant="secondary" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>前へ</Button>
          <Button variant="secondary" size="sm" disabled={!data?.has_more} onClick={() => setOffset(offset + PAGE_SIZE)}>次へ</Button>
        </div>
      )}
    </div>
  );
}
